// The name generator (NameGeneratorViews.swift): names from the chosen lists, drawn again whenever a choice changes.
// Choose some and Create Character adds them to the cast (with study pages, if the project keeps them); names already
// in the cast are faded with a tick. The choices (who, shape, keep or match, how unusual, which lists) and the writer's
// own lists are kept in this browser, as the Mac keeps them on the device. On a phone the choices open from Choices.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Project } from '@writing-suite/core';
import {
  BUILT_IN_LISTS, BUILT_IN_ORIGINS, fullName, generateNames, NAME_LIST_KIND_LABELS, nameListTitle, parseNames,
  type FilterMode, type GeneratedName, type NameFilter, type NameList, type NameListKind, type NameOptions,
} from '@writing-suite/core/story';
import { announce } from '../../shell/context.ts';
import type { LiveProject } from '../../store/project.ts';
import { allNameLists, castNames, loadNameOptions, loadOwnLists, ownList, saveNameOptions, saveOwnLists, storyEdit } from '../../store/story.ts';
import { MenuPopup } from '../Menu.tsx';
import { plural, StorySheet } from './common.tsx';

export function NameGenerator({ project, live, onClose, onShow }: {
  project: Project;
  live: LiveProject | null;
  onClose: () => void;
  /** Open a created character: its study (`study`) or Cast & Places. */
  onShow: (entityId: string, study: boolean) => void;
}) {
  const [options, setOptionsState] = useState<NameOptions>(loadNameOptions);
  const [own, setOwnState] = useState<NameList[]>(loadOwnLists);
  const [drawn, setDrawn] = useState<{ names: GeneratedName[]; note: string | null }>({ names: [], note: null });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [created, setCreated] = useState<{ id: string; name: string; study: boolean }[]>([]);
  const [choicesOpen, setChoicesOpen] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; index: number } | null>(null);
  const lists = useMemo(() => allNameLists(own), [own]);
  const cast = castNames(project);
  const draw = (o = options, l = lists) => {
    setDrawn(generateNames(o, l));
    setSelected(new Set());
  };
  const setOptions = (change: Partial<NameOptions>) => {
    const next = { ...options, ...change };
    setOptionsState(next);
    saveNameOptions(next);
    draw(next);
  };
  const setOwn = (next: NameList[], listIDs = options.listIDs) => {
    setOwnState(next);
    saveOwnLists(next);
    const o = { ...options, listIDs };
    setOptionsState(o);
    saveNameOptions(o);
    draw(o, allNameLists(next));
  };
  const drewOnce = useRef(false);
  useEffect(() => {
    if (drewOnce.current) return;
    drewOnce.current = true;
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const names = drawn.names.map(fullName);
  const chosen = [...selected].sort((a, b) => a - b).map((i) => names[i]).filter((n) => n !== undefined);
  const toCreate = chosen.filter((n) => !cast.has(n.toLowerCase()));
  const create = (picked: string[]) => {
    if (!live || !picked.length) return;
    const made = storyEdit(live, (e) => e.createCharacters(picked));
    if (!made.length) return;
    setCreated(made.map((e) => ({ id: e.id, name: e.name, study: e.studyID !== undefined })));
    setSelected(new Set([...selected].filter((i) => !picked.includes(names[i]))));
    announce(made.length === 1 ? `Added ${made[0].name} to Cast & Places.` : `Added ${made.length} characters to Cast & Places.`);
  };
  const copy = (picked: string[]) => void navigator.clipboard?.writeText(picked.join('\n')).then(() => announce(picked.length === 1 ? 'Name copied' : 'Names copied')).catch(() => {});
  const toggle = (i: number) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  return (
    <StorySheet title="Name generator" onClose={onClose} className="name-generator" labelledBy="name-generator-title">
      <div
        className={`names-layout${choicesOpen ? ' choices-open' : ''}`}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'g') {
            e.preventDefault();
            draw();
          }
        }}
      >
        <header className="names-head">
          <div>
            <h2 id="name-generator-title" className="story-sheet-title">
              Name generator
            </h2>
            <p className="page-sub">Choose a name to make them a character in {project.title || 'the project'}.</p>
          </div>
          <button type="button" className="quiet-button names-choices-toggle" aria-expanded={choicesOpen} onClick={() => setChoicesOpen(!choicesOpen)}>
            Choices
          </button>
          <button type="button" className="quiet-button" onClick={onClose}>
            Done
          </button>
        </header>
        <div className="names-results">
          {drawn.note ? <p className="names-note" role="status">{drawn.note}</p> : null}
          <ul className="names-grid" aria-label="Names">
            {names.map((n, i) => {
              const inCast = cast.has(n.toLowerCase());
              const on = selected.has(i);
              return (
                <li key={`${i}|${n}`}>
                  <button
                    type="button"
                    className={`name-cell${on ? ' selected' : ''}${inCast ? ' in-cast' : ''}`}
                    aria-pressed={on}
                    title={inCast ? 'Already in the cast' : undefined}
                    data-name={n}
                    onClick={() => toggle(i)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setMenu({ x: e.clientX, y: e.clientY, index: i });
                    }}
                  >
                    <span className="name-text">{n}</span>
                    {inCast ? <span className="name-tick" aria-label="In the cast">✓</span> : on ? <span className="name-tick chosen" aria-hidden="true">●</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {created.length ? (
          <div className="names-created" role="status">
            <span>{created.length === 1 ? `Added ${created[0].name} to Cast & Places.` : `Added ${created.length} characters to Cast & Places.`}</span>
            {created.length === 1 && created[0].study ? (
              <button type="button" className="quiet-button" onClick={() => onShow(created[0].id, true)}>
                Open Study
              </button>
            ) : (
              <button type="button" className="quiet-button" onClick={() => onShow(created[0].id, false)}>
                Show Cast
              </button>
            )}
          </div>
        ) : null}
        <footer className="names-foot">
          <label className="names-count">
            <span>{plural(options.count, 'name')}</span>
            <input type="range" min={1} max={500} value={options.count} aria-label="Number of names" onChange={(e) => setOptions({ count: Number(e.target.value) })} />
          </label>
          <button type="button" className="quiet-button" title="Draw a fresh set of names (⌘G)" aria-keyshortcuts="Meta+G Control+G" onClick={() => draw()}>
            New Names
          </button>
          <span className="names-foot-actions">
            {chosen.length ? (
              <button type="button" className="quiet-button" onClick={() => copy(chosen)}>
                Copy
              </button>
            ) : null}
            <button type="button" className="button primary" disabled={!toCreate.length || !live} onClick={() => create(toCreate)}>
              {toCreate.length > 1 ? `Create ${toCreate.length} Characters` : 'Create Character'}
            </button>
          </span>
        </footer>
        <aside className="names-choices" aria-label="Choices">
          <NameChoices options={options} setOptions={setOptions} own={own} setOwn={setOwn} />
        </aside>
      </div>
      {menu ? (
        <MenuPopup
          label="Name"
          at={menu}
          onClose={() => setMenu(null)}
          entries={[
            { label: 'Create Character', disabled: !live || cast.has(names[menu.index]?.toLowerCase() ?? ''), onSelect: () => create([names[menu.index]]) },
            { label: 'Copy', onSelect: () => copy([names[menu.index]]) },
          ]}
        />
      ) : null}
    </StorySheet>
  );
}

const FILTER_MODES: { mode: FilterMode; label: (part: string) => string }[] = [
  { mode: 'any', label: (p) => `Any ${p}` },
  { mode: 'exactly', label: (p) => `${p[0].toUpperCase()}${p.slice(1)} is` },
  { mode: 'startsWith', label: () => 'Starts with' },
  { mode: 'endsWith', label: () => 'Ends with' },
  { mode: 'contains', label: () => 'Contains' },
];

function NameChoices({ options, setOptions, own, setOwn }: {
  options: NameOptions;
  setOptions: (c: Partial<NameOptions>) => void;
  own: NameList[];
  setOwn: (lists: NameList[], listIDs?: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const included = (id: string) => options.listIDs.includes(id);
  const setIncluded = (id: string, on: boolean) => setOptions({ listIDs: on ? [...options.listIDs.filter((x) => x !== id), id] : options.listIDs.filter((x) => x !== id) });
  const caption = options.obscurity >= 10 ? 'Every name in the lists.' : options.obscurity === 1 ? 'Only the most common tenth of each list.' : `The most common ${options.obscurity * 10}% of each list.`;
  const filter = (part: 'forename' | 'surname', example: string) => {
    const f: NameFilter = options[part];
    return (
      <div className="names-filter">
        <select aria-label={`${part[0].toUpperCase()}${part.slice(1)}`} value={f.mode} onChange={(e) => setOptions({ [part]: { ...f, mode: e.target.value as FilterMode } })}>
          {FILTER_MODES.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label(part)}
            </option>
          ))}
        </select>
        <input type="text" placeholder={example} value={f.text} disabled={f.mode === 'any'} aria-label={`${part[0].toUpperCase()}${part.slice(1)} letters`} autoComplete="off" spellCheck={false} onChange={(e) => setOptions({ [part]: { ...f, text: e.target.value } })} />
      </div>
    );
  };
  return (
    <div className="names-panel">
      <fieldset className="names-group">
        <legend className="eyebrow">Who</legend>
        <div className="segmented" role="radiogroup" aria-label="Who">
          {(['female', 'male', 'either'] as const).map((g) => (
            <button key={g} type="button" role="radio" aria-checked={options.gender === g} className={options.gender === g ? 'on' : ''} onClick={() => setOptions({ gender: g })}>
              {g === 'female' ? 'Women' : g === 'male' ? 'Men' : 'Either'}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="names-group">
        <legend className="eyebrow">Shape</legend>
        <label className="names-row">
          <span>{['One forename', 'Two forenames', 'Three forenames'][options.forenameCount - 1]}</span>
          <input type="number" min={1} max={3} value={options.forenameCount} aria-label="Forenames" onChange={(e) => setOptions({ forenameCount: Math.min(3, Math.max(1, Number(e.target.value) || 1)) })} />
        </label>
        <label className="names-row">
          <input type="checkbox" checked={options.initialsOnly} onChange={(e) => setOptions({ initialsOnly: e.target.checked })} /> Initials instead of forenames
        </label>
        <label className="names-row">
          <input type="checkbox" checked={options.doubleBarrelled} onChange={(e) => setOptions({ doubleBarrelled: e.target.checked })} /> Double-barrelled surnames
        </label>
        <label className="names-row">
          <input type="checkbox" checked={options.alliterate} onChange={(e) => setOptions({ alliterate: e.target.checked })} /> Alliterate (Peter Parker)
        </label>
      </fieldset>
      <fieldset className="names-group">
        <legend className="eyebrow">Keep or match</legend>
        {filter('forename', 'Ilse')}
        {filter('surname', 'Weir')}
      </fieldset>
      <fieldset className="names-group">
        <legend className="eyebrow">How unusual</legend>
        <input type="range" min={1} max={10} step={1} value={options.obscurity} aria-label="How unusual" aria-valuetext={caption} onChange={(e) => setOptions({ obscurity: Number(e.target.value) })} />
        <div className="names-scale" aria-hidden="true">
          <span>Common</span>
          <span>Anything</span>
        </div>
        <p className="names-caption">{caption}</p>
      </fieldset>
      <fieldset className="names-group">
        <legend className="eyebrow">Name lists</legend>
        <table className="names-lists">
          <thead>
            <tr>
              <th scope="col"><span className="visually-hidden">List</span></th>
              <th scope="col">Women</th>
              <th scope="col">Men</th>
              <th scope="col">Surnames</th>
            </tr>
          </thead>
          <tbody>
            {BUILT_IN_ORIGINS.map((origin) => (
              <tr key={origin}>
                <th scope="row">{origin}</th>
                {(['female', 'male', 'surnames'] as NameListKind[]).map((kind) => {
                  const list = BUILT_IN_LISTS.find((l) => l.origin === origin && l.kind === kind);
                  return (
                    <td key={kind}>
                      {list ? <input type="checkbox" aria-label={nameListTitle(list)} checked={included(list.id)} onChange={(e) => setIncluded(list.id, e.target.checked)} /> : <span aria-hidden="true">–</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {own.length ? (
          <>
            <p className="eyebrow names-own-title">Your lists</p>
            <ul className="names-own">
              {own.map((l) => (
                <li key={l.id}>
                  <label>
                    <input type="checkbox" checked={included(l.id)} onChange={(e) => setIncluded(l.id, e.target.checked)} />
                    <span>
                      {l.origin}
                      <small>
                        {NAME_LIST_KIND_LABELS[l.kind]} · {plural(l.names.length, 'name')}
                      </small>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="link-button danger"
                    aria-label={`Remove ${l.origin}`}
                    onClick={() => {
                      if (confirm(`Remove “${l.origin}”? Characters already made keep their names.`)) setOwn(own.filter((x) => x.id !== l.id), options.listIDs.filter((x) => x !== l.id));
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {adding ? (
          <OwnListForm
            onCancel={() => setAdding(false)}
            onAdd={(list) => {
              setAdding(false);
              setOwn([...own, list], [...options.listIDs, list.id]);
            }}
          />
        ) : (
          <button type="button" className="link-button" onClick={() => setAdding(true)}>
            + Add your own list…
          </button>
        )}
      </fieldset>
    </div>
  );
}

/** Your own list: a name, what it holds, and names typed, pasted or read from a text file, most common first. */
function OwnListForm({ onAdd, onCancel }: { onAdd: (l: NameList) => void; onCancel: () => void }) {
  const [origin, setOrigin] = useState('');
  const [kind, setKind] = useState<NameListKind>('anyone');
  const [text, setText] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const count = parseNames(text).length;
  return (
    <form
      className="names-own-form"
      aria-label="New name list"
      onSubmit={(e) => {
        e.preventDefault();
        if (origin.trim() && count) onAdd(ownList(origin, kind, text));
      }}
    >
      <label>
        <span className="eyebrow">Name</span>
        <input type="text" placeholder="Weir village" value={origin} onChange={(e) => setOrigin(e.target.value)} />
      </label>
      <label>
        <span className="eyebrow">Holds</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as NameListKind)}>
          {(Object.keys(NAME_LIST_KIND_LABELS) as NameListKind[]).map((k) => (
            <option key={k} value={k}>
              {NAME_LIST_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="eyebrow">Names</span>
        <textarea rows={6} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} />
      </label>
      <p className="names-caption">One name per line, or separated by commas, most common first. {plural(count, 'name')}.</p>
      <label className="names-file">
        Read a text file…
        <input
          type="file"
          accept=".txt,.csv,text/plain,text/csv"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const contents = await file.text();
              setText(text ? `${text}\n${contents}` : contents);
              if (!origin) setOrigin(file.name.replace(/\.[^.]+$/, ''));
              setFailure(null);
            } catch {
              setFailure(`Couldn’t read “${file.name}” as text.`);
            }
          }}
        />
      </label>
      {failure ? <p className="error">{failure}</p> : null}
      <div className="actions">
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="button primary" disabled={!origin.trim() || !count}>
          Add
        </button>
      </div>
    </form>
  );
}
